import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtErr, avatarSrc, wsUrl } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, X, Check } from "lucide-react";
import RevealAvatar from "../components/app/RevealAvatar";
import { logSwallow } from "../lib/log";

export default function MatchCelebration() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [youRevealed, setYouRevealed] = useState(false);
  const [theyRevealed, setTheyRevealed] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [flash, setFlash] = useState(true);
  // Two-step reveal UX: confirm modal → cinematic overlay → resolve state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selfRevealFx, setSelfRevealFx] = useState(false);
  const wsRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/matches/${id}`);
      setOther(data.other_user);
      setYouRevealed(!!data.you_revealed);
      setTheyRevealed(!!data.they_revealed);
    } catch (e) { toast.error(fmtErr(e)); nav("/home"); }
  };

  useEffect(() => {
    load();
    const t = setTimeout(() => setFlash(false), 450);
    // Realtime: if the other side reveals while we're on this screen, refresh
    // so their photo/name dissolve-in via <RevealAvatar> without a manual reload.
    let ws = null;
    try {
      const url = wsUrl("/user");
      if (url) {
        ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.type === "match.identity_revealed" && m.match_id === id) load();
          } catch (e) { logSwallow("match.ws.parse", e); }
        };
        ws.onerror = (e) => logSwallow("match.ws.error", e);
        wsRef.current = ws;
      }
    } catch (e) { logSwallow("match.ws.open", e); }
    return () => {
      clearTimeout(t);
      if (ws) { try { ws.close(); } catch (e) { logSwallow("match.ws.close", e); } }
      if (wsRef.current === ws) wsRef.current = null;
    };
    // load is stable per-id; adding it as dep would recreate the effect every render
  }, [id]);

  const doReveal = async () => {
    setConfirmOpen(false);
    if (youRevealed) return;
    setRevealing(true);
    try {
      await api.post(`/matches/${id}/reveal`);
      // Kick off cinematic overlay showing THIS user's own real photo + name.
      // It's a confirmation moment — "this is what they're about to see."
      setSelfRevealFx(true);
      setYouRevealed(true);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setRevealing(false); }
  };

  const inGhost = (user?.mode_default || "anonymous") !== "real";

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex flex-col items-center justify-center">
      {flash && <div className="absolute inset-0 bg-[#E60000] opacity-80 z-50 pointer-events-none animate-[matchFlash_0.45s_ease-out_forwards]" data-testid="match-flash" />}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(102,0,0,0.45)_0%,rgba(0,0,0,1)_70%)] pointer-events-none" />

      <div className="relative w-full max-w-md px-7 text-center" data-testid="match-content">
        <p className="font-mono-accent text-zinc-400" style={{ letterSpacing: "0.45em" }} data-testid="match-overline">
          ENCRYPTED CONNECTION
        </p>

        <h1
          className="mt-6 text-6xl sm:text-7xl font-bold tracking-tight leading-[0.95]"
          style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 800, letterSpacing: "-0.04em" }}
          data-testid="match-title"
        >
          It&apos;s a match.
        </h1>

        {/* Identity reveal: cinematic blur→sharp + typewriter name — only fires
            when the SERVER has decided this viewer may see the other user's
            real identity (Visible mode OR opt-in reveal via /matches/{id}/reveal).
            Ghost/hidden case falls back to the neutral EyeHeart trio. */}
        <div className="mt-14 flex items-center justify-center gap-6" data-testid="match-trio">
          {other?.real_name ? (
            <RevealAvatar
              src={avatarSrc(other)}
              name={other.real_name}
              size={112}
              showName={false}
              testid="match-other-avatar"
            />
          ) : (
            <>
              <EyeHeart />
              <HeartShape />
              <EyeHeart />
            </>
          )}
        </div>

        {other?.real_name && (
          <div
            className="mt-6 flex justify-center"
            data-testid="match-name-reveal-wrap"
          >
            <RevealNameLine name={other.real_name} />
          </div>
        )}

        <p
          className="mt-10 text-3xl font-bold text-[#22C55E] tracking-tight"
          style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 700 }}
          data-testid="match-status"
        >
          {t("match.mutualInterest")}
        </p>
        <p className="mt-2 text-zinc-400 text-lg" data-testid="match-sub">
          {t("match.chatPrivatelySub")}
          {other && !other.real_name && (
            <span className="block mt-1 text-zinc-500 text-sm font-mono-accent tracking-wider" data-testid="match-other-alias">
              {other.display_name || "—"}
            </span>
          )}
          {other?.real_name && (other.age || other.hometown) && (
            <span className="block mt-1 text-zinc-500 text-sm font-mono-accent tracking-wider" data-testid="match-other-meta">
              {other.age ? <span>{other.age}</span> : null}
              {other.age && other.hometown ? <span className="text-zinc-600"> · </span> : null}
              {other.hometown ? <span>{other.hometown}</span> : null}
            </span>
          )}
        </p>

        {/* Opt-in reveal (B3) — only meaningful when user is currently in Ghost mode */}
        {inGhost && (
          <div className="mt-8 glass rounded-2xl p-4 text-left" data-testid="reveal-block">
            <div className="flex items-center gap-3 mb-2">
              {youRevealed ? <Eye size={18} className="text-[#22C55E]" /> : <EyeOff size={18} className="text-zinc-400" />}
              <p className="font-mono-accent text-xs tracking-wider text-zinc-400">{t("match.ghostBoundary")}</p>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              {youRevealed
                ? t("match.revealedNote")
                : t("match.notRevealedNote")}
            </p>
            {!youRevealed && (
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={revealing}
                data-testid="reveal-btn"
                className="mt-3 w-full py-3 rounded-full border border-[#E60000] text-white font-semibold text-sm tracking-wider hover:bg-[#E60000]/10 transition disabled:opacity-50"
              >
                {revealing ? "…" : t("match.revealYourself")}
              </button>
            )}
            <p className="mt-3 text-xs text-zinc-500" data-testid="reveal-they-status">
              {theyRevealed ? t("match.theyRevealed") : t("match.theyNotRevealed")}
            </p>
          </div>
        )}

        <button
          onClick={() => nav(`/chat/${id}`)}
          data-testid="match-chat-btn"
          className="mt-8 w-full py-5 rounded-full bg-zinc-200 hover:bg-white text-black font-bold tracking-wider transition active:scale-[0.98] shadow-[0_0_30px_rgba(255,255,255,0.1)]"
        >
          {t("match.chatPrivately")}
        </button>

        <button
          onClick={() => nav("/home")}
          data-testid="match-back-hub"
          className="mt-6 font-mono-accent text-zinc-500 hover:text-white tracking-[0.3em] transition text-xs"
        >
          {t("match.backToHub")}
        </button>
      </div>

      {confirmOpen && (
        <RevealConfirmDialog
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doReveal}
          t={t}
        />
      )}

      {selfRevealFx && (
        <SelfRevealConfirm onClose={() => setSelfRevealFx(false)} t={t} />
      )}

      <style>{`
        @keyframes matchFlash {
          0%   { opacity: 0.85; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/**
 * RevealConfirmDialog — replaces the native `window.confirm` with a
 * dark/red-themed modal that matches the app aesthetic and clearly states
 * the privacy contract (per-match, permanent, does NOT toggle Ghost globally).
 */
function RevealConfirmDialog({ onCancel, onConfirm, t }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/85 backdrop-blur-sm animate-[fadeIn_180ms_ease-out_both]"
      data-testid="reveal-confirm"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-sm rounded-2xl border border-[#E60000]/30 bg-[#0b0202]/95 p-6 shadow-[0_0_60px_rgba(230,0,0,0.35)]">
        <button
          onClick={onCancel}
          data-testid="reveal-confirm-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-white"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <p className="font-mono-accent text-[10px] tracking-[0.3em] text-[#E60000]">{t("match.ghostBoundary")}</p>
        <h3 className="mt-2 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 700 }}>
          {t("match.confirmTitle")}
        </h3>
        <ul className="mt-4 space-y-2 text-sm text-zinc-400 leading-relaxed">
          <li>· {t("match.confirmLi1")}</li>
          <li>· {t("match.confirmLi2")}</li>
          <li>· {t("match.confirmLi3")}</li>
        </ul>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            data-testid="reveal-cancel"
            className="py-3 rounded-full border border-white/15 text-zinc-300 text-sm font-semibold tracking-wider hover:border-white/30 transition"
          >
            {t("match.notYet")}
          </button>
          <button
            onClick={onConfirm}
            data-testid="reveal-confirm-yes"
            className="py-3 rounded-full bg-[#E60000] hover:brightness-110 text-white text-sm font-bold tracking-wider transition"
          >
            {t("match.reveal")}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  );
}

/**
 * SelfRevealConfirm — the user who just tapped "Reveal yourself" should NOT
 * see their own face being unblurred (that cinematic moment is reserved for
 * the OTHER person, who sees it via <RevealAvatar> when the WS event fires).
 * On this side we show a clean confirmation: a check + "You've been revealed
 * to this match" + a note that their Ghost mode elsewhere is unchanged.
 * Auto-dismisses at 2.4s or on tap.
 */
function SelfRevealConfirm({ onClose, t }) {
  useEffect(() => {
    const t = setTimeout(onClose, 2400);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-6 bg-black/85 backdrop-blur-sm animate-[fadeIn_220ms_ease-out_both]"
      onClick={onClose}
      data-testid="self-reveal-confirm"
      role="status"
      aria-live="polite"
    >
      <div
        className="relative w-full max-w-sm rounded-2xl border border-[#E60000]/40 bg-[#0b0202]/95 p-8 text-center shadow-[0_0_60px_rgba(230,0,0,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated check burst */}
        <div className="mx-auto flex items-center justify-center" style={{ width: 72, height: 72 }}>
          <div
            className="absolute rounded-full border-2 border-[#E60000]/40"
            style={{ width: 72, height: 72, animation: "ringPulse 1400ms ease-out infinite" }}
            aria-hidden="true"
          />
          <div className="relative w-16 h-16 rounded-full bg-[#E60000] flex items-center justify-center shadow-[0_0_28px_rgba(230,0,0,0.55)]">
            <Check size={28} strokeWidth={3} className="text-white" />
          </div>
        </div>

        <p className="mt-6 font-mono-accent text-[10px] tracking-[0.4em] text-[#E60000]" data-testid="self-reveal-overline">
          {t("match.revealed")}
        </p>
        <h3
          className="mt-2 text-2xl font-bold tracking-tight text-white"
          style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 700 }}
          data-testid="self-reveal-title"
        >
          {t("match.selfRevealTitle")}
        </h3>
        <p className="mt-3 text-sm text-zinc-400 leading-relaxed" data-testid="self-reveal-body">
          {t("match.selfRevealBody")}
        </p>
        <button
          onClick={onClose}
          data-testid="self-reveal-dismiss"
          className="mt-6 w-full py-3 rounded-full border border-white/15 text-zinc-300 text-xs font-semibold tracking-[0.3em] hover:border-white/30 hover:text-white transition"
        >
          GOT IT
        </button>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes ringPulse {
          0%   { transform: scale(1);   opacity: 0.9; }
          70%  { transform: scale(1.5); opacity: 0;   }
          100% { transform: scale(1.5); opacity: 0;   }
        }
      `}</style>
    </div>
  );
}

function RevealNameLine({ name }) {
  // Stand-alone typewriter to accompany the RevealAvatar (which we render with
  // showName=false above so the name has room to breathe below the photo).
  const [typed, setTyped] = useState(_seenLines.has(name) ? name : "");
  useEffect(() => {
    if (_seenLines.has(name)) return undefined;
    let i = 0;
    const perChar = 55;
    let cancelled = false;
    // Small delay so the typewriter starts as the photo begins to sharpen
    const startDelay = 550;
    const startTimer = setTimeout(() => {
      const interval = setInterval(() => {
        if (cancelled) { clearInterval(interval); return; }
        i += 1;
        setTyped(name.slice(0, i));
        if (i >= name.length) {
          clearInterval(interval);
          _seenLines.add(name);
        }
      }, perChar);
    }, startDelay);
    return () => { cancelled = true; clearTimeout(startTimer); };
  }, [name]);

  const done = typed.length >= (name || "").length;
  return (
    <p
      className="text-white text-3xl font-bold tracking-tight text-center"
      style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 700, letterSpacing: "-0.02em" }}
      data-testid="match-name-reveal"
      aria-label={name}
    >
      <span>{typed}</span>
      <span
        className={`inline-block w-[2px] h-7 align-middle bg-white ml-1 ${done ? "opacity-0" : "animate-pulse"}`}
        aria-hidden="true"
      />
    </p>
  );
}
const _seenLines = new Set();

function EyeHeart() {  return (
    <svg viewBox="0 0 100 60" className="w-20 h-12" data-testid="eye-heart">
      <ellipse cx="50" cy="30" rx="46" ry="22" fill="#ffffff" />
      <circle cx="50" cy="30" r="14" fill="#0b0202" stroke="#000" strokeWidth="1.5" />
      <path
        d="M50 39 C 45 35, 39 32, 39 26 C 39 22, 43 19, 46 21 C 48 22, 49 24, 50 26 C 51 24, 52 22, 54 21 C 57 19, 61 22, 61 26 C 61 32, 55 35, 50 39 Z"
        fill="#ffffff"
      />
    </svg>
  );
}

function HeartShape() {
  return (
    <svg viewBox="0 0 60 56" className="w-10 h-10" data-testid="heart-shape">
      <path
        d="M30 50 C 12 38, 2 28, 2 16 C 2 8, 9 2, 16 2 C 22 2, 27 6, 30 12 C 33 6, 38 2, 44 2 C 51 2, 58 8, 58 16 C 58 28, 48 38, 30 50 Z"
        fill="#ffffff"
      />
    </svg>
  );
}
